import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { CellLabel, useLang } from '@shapeable/ui';
import { classNames } from '@shapeable/utils';
import { STRESSORS_COLOUR_MAP } from '../../data';

const cls = classNames('stressors-legend');

const STRESSOR_SCALE = Object.entries(STRESSORS_COLOUR_MAP)
  .sort(([a], [b]) => Number(a) - Number(b));

export type StressorsLegendProps = Classable & HasChildren & {
};

export const StressorsLegendDefaultProps: StressorsLegendProps = {
};

type SwatchProps = {
  _color: string;
};

const ContainerStyles = breakpoints({
  base: css`
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.UNIT(1)};
    max-width: 100%;
    box-sizing: border-box;
    padding-bottom: ${theme.UNIT(4)};
  `,
  tablet: css`
    gap: ${theme.UNIT(1.5)};
  `,
});

const TitleStyles = breakpoints({
  base: css`
    margin: 0;
    font-size: ${theme.FONT_SIZE(10)};
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${theme.COLOR('mid')};
    line-height: 1.2;
  `,
  tablet: css`
    font-size: ${theme.FONT_SIZE(11)};
  `,
});

const ScaleStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.UNIT(0.75)} ${theme.UNIT(1.5)};
  `,
});

const ItemStyles = breakpoints({
  base: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.UNIT(1)};
  `,
});

const LabelStyles = breakpoints({
  base: css`
    font-size: ${theme.FONT_SIZE(10)};
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    color: ${theme.COLOR('mid')};
    min-width: 0.65em;
    text-align: right;
  `,
  tablet: css`
    font-size: ${theme.FONT_SIZE(11)};
  `,
});

const SwatchStyles = breakpoints({
  base: css`
    width: 18px;
    height: 11px;
    border-radius: 2px;
    flex-shrink: 0;
    background: ${({ _color }: SwatchProps) => _color};
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.12);
  `,
  tablet: css`
    width: 20px;
    height: 14px;
  `,
});

const My = {
  Container: styled.div`${ContainerStyles}`,
  Title: styled(CellLabel)`${TitleStyles}`,
  Scale: styled.div`${ScaleStyles}`,
  Item: styled.div`${ItemStyles}`,
  Label: styled.span`${LabelStyles}`,
  Swatch: styled.span<SwatchProps>`${SwatchStyles}`,
};

export const StressorsLegend: Shapeable.FC<StressorsLegendProps> = (props) => {
  const { className } = props;
  const t = useLang();

  return (
    <My.Container className={cls.name(className)} role="img" aria-label={t('Stressor mean score legend')}>
      <My.Title>{t('Stressor mean score')}</My.Title>
      <My.Scale>
        {STRESSOR_SCALE.map(([score, color]) => (
          <My.Item key={score}>
            <My.Label>{score}</My.Label>
            <My.Swatch _color={color} title={score} />
          </My.Item>
        ))}
      </My.Scale>
    </My.Container>
  );
};

StressorsLegend.cls = cls;
StressorsLegend.defaultProps = StressorsLegendDefaultProps;
